import { reactive } from 'vue';

import { dispatchBugIAmUserClear, dispatchBugIAmUserSet } from '@/integrations/bugiam-events';

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  role: string;
  isLocked: boolean;
  twoFactorEnabled?: boolean;
}

interface AuthState {
  checked: boolean;
  user: AuthUser | null;
}

export const authState = reactive<AuthState>({
  checked: false,
  user: null,
});

export async function loadCurrentUser(): Promise<AuthUser | null> {
  if (authState.checked) {
    return authState.user;
  }

  try {
    const response = await fetch('/api/auth/me');

    if (!response.ok) {
      authState.user = null;
      dispatchBugIAmUserClear();
      return null;
    }

    const data = (await response.json()) as { user: AuthUser };
    authState.user = data.user;
    dispatchBugIAmUserSet(data.user);
    return data.user;
  } finally {
    authState.checked = true;
  }
}

export class TwoFactorRequiredError extends Error {
  constructor() {
    super('2FA-Code erforderlich.');
    this.name = 'TwoFactorRequiredError';
  }
}

export async function login(email: string, password: string, twoFactorCode?: string, twoFactorRecoveryCode?: string): Promise<AuthUser> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password, twoFactorCode, twoFactorRecoveryCode }),
  });

  const data = (await response.json().catch(() => ({}))) as {
    user?: AuthUser;
    requiresTwoFactor?: boolean;
    statusMessage?: string;
    message?: string;
  };

  if (!response.ok) {
    throw new Error(data.statusMessage ?? data.message ?? 'Email oder Passwort stimmen nicht.');
  }

  if (data.requiresTwoFactor) {
    throw new TwoFactorRequiredError();
  }

  if (!data.user) {
    throw new Error('Login fehlgeschlagen.');
  }

  authState.user = data.user;
  authState.checked = true;
  dispatchBugIAmUserSet(data.user);
  return data.user;
}

export async function loginWithPasskey(email: string | undefined, credential: unknown): Promise<AuthUser> {
  const response = await fetch('/api/auth/passkey/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...(email ? { email } : {}), credential }),
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { statusMessage?: string; message?: string };
    throw new Error(data.statusMessage ?? data.message ?? 'Passkey Login fehlgeschlagen.');
  }

  const data = (await response.json()) as { user: AuthUser };
  authState.user = data.user;
  authState.checked = true;
  dispatchBugIAmUserSet(data.user);
  return data.user;
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST' });
  authState.user = null;
  authState.checked = true;
  dispatchBugIAmUserClear();
}
